import { animated, useSpring } from '@react-spring/web';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { BrandS, LabelL } from '@sorare/core/src/atoms/typography';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

const Wrapper = styled.div`
  border-radius: var(--unit);
  background: linear-gradient(
    92deg,
    #fff 0%,
    #eabfe2 28.88%,
    #fff 48.67%,
    #bedecb 64.58%,
    #fff 100%
  );
`;

const Title = styled(BrandS)`
  padding: var(--half-unit) var(--unit);
  text-transform: uppercase;
  color: var(--c-nl-900);
`;

const ShinyMVP = () => {
  return (
    <Wrapper>
      <Title>
        <FormattedMessage {...glossary.mvp} />
      </Title>
    </Wrapper>
  );
};

const AnimatedWrapper = styled(animated.div)`
  display: flex;
  background: var(--c-nd-50);
  backdrop-filter: blur(32px);
  border: 1px solid var(--c-nl-200);

  padding: var(--double-unit) 0 var(--double-unit) var(--double-unit);
  border-radius: var(--double-unit);
  margin: var(--double-unit) var(--unit);
  text-align: start;
  align-items: center;
  gap: var(--intermediate-unit);

  @media ${laptopAndAbove} {
    position: relative;
    background: var(--c-nd-200);
    margin: var(--double-unit);
  }

  button {
    margin-left: auto;
  }
`;

export const MVPBenchOnboarding = () => {
  const isLaptopAndAbove = useIsLaptop();
  const [isRendered, setIsRendered] = useState(true);
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const isOpen = !lifecycle?.sawMVPOnboarding;

  const springProps = useSpring({
    from: {
      transform: `translateY(${isLaptopAndAbove ? '-100%' : '100%'})`,
      opacity: 0,
    },
    to: {
      transform: isOpen
        ? 'translateY(0%)'
        : `translateY(${isLaptopAndAbove ? '-100%' : '100%'})`,
      opacity: isOpen ? 1 : 0,
    },
    config: { tension: 300, friction: 20 },
    onRest: () => {
      if (!isOpen) setIsRendered(false);
    },
  });

  const responsiveProps = isLaptopAndAbove
    ? {}
    : { fullWidth: true, anchor: 'top' as const };

  if (!isRendered) return null;

  return (
    <Portal id="above-compose-footer-portal">
      <AnimatedWrapper style={springProps} {...responsiveProps}>
        <ShinyMVP />
        <LabelL>
          <FormattedMessage defaultMessage="Your highest L10 player is excluded from the Points Cap" />
        </LabelL>
        <IconButton
          color="transparent"
          size="medium"
          onClick={() => {
            updateLifecycle(LIFECYCLE.sawMVPOnboarding, true);
          }}
        >
          <FontAwesomeIcon icon={iconX} size="xl" />
        </IconButton>
      </AnimatedWrapper>
    </Portal>
  );
};
